import { usePageRefresh } from '../../../../../context/PageRefreshContext';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react';
import {
    getSocietyBanks,
    createSocietyBank,
    updateSocietyBank,
    deleteSocietyBank,
} from "../../../../../redux/actions/SocietyPortal/SocietyBankAction/SocietyBankAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import Breadcrumb from "../../../../../components/Breadcrumb";
import BankAccountModal from "./BankAccountModal";

const BankAccounts = () => {
    const dispatch = useDispatch();
    const { banksData, banksPagination, loading, createLoading, updateLoading, deleteLoading } = useSelector(state => state.SocietyBankReducer || {});

    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        account_category: "SAVING",
        bank_name: "",
        branch_name: "",
        account_number: "",
        ifsc_code: "",
        account_holder_name: "",
        is_default: 0,
    });
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState("list");

    const societyId = selectedSocietyStorer.getSocietyId();

    // Fetch bank accounts
    useEffect(() => {
        if (societyId) {
            dispatch(getSocietyBanks({ society_id: societyId, page, limit: 20 }));
        }
    }, [dispatch, societyId, page]);

    const refreshList = () => {
        dispatch(getSocietyBanks({ society_id: societyId, page, limit: 20 }));
    };
    usePageRefresh(refreshList);

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            society_id: Number(societyId),
            is_default: formData.is_default ? 1 : 0,
            onSuccess: () => {
                handleCloseModal();
                refreshList();
            }
        };

        if (editId) {
            dispatch(updateSocietyBank({ id: editId, ...payload }));
        } else {
            dispatch(createSocietyBank(payload));
        }
    };

    const handleEdit = (item) => {
        setFormData({
            account_category: item.account_category || "SAVING",
            bank_name: item.bank_name || "",
            branch_name: item.branch_name || "",
            account_number: item.account_number || "",
            ifsc_code: item.ifsc_code || "",
            account_holder_name: item.account_holder_name || "",
            is_default: item.is_default || 0,
        });
        setEditId(item.id);
        setShowModal(true);
    };

    const handleDelete = (item) => {
        if (window.confirm(`Are you sure you want to delete "${item.bank_name}"? This will also deactivate the associated GL account.`)) {
            dispatch(deleteSocietyBank({
                id: item.id,
                society_id: societyId,
                onSuccess: () => refreshList()
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            account_category: "SAVING",
            bank_name: "",
            branch_name: "",
            account_number: "",
            ifsc_code: "",
            account_holder_name: "",
            is_default: 0,
        });
        setEditId(null);
    };

    const handleSetDefault = (item) => {
        dispatch(updateSocietyBank({
            id: item.id,
            society_id: Number(societyId),
            is_default: 1,
            onSuccess: () => refreshList()
        }));
    };

    const accounts = banksData || [];
    const bankCount = banksPagination?.total ?? accounts.length;
    // The list puts the default first, so it is on this page if there is one.
    const hasDefault = accounts.some((a) => a.is_default === 1);
    const editingDefault = !!editId && accounts.find((a) => a.id === editId)?.is_default === 1;
    // The backend makes the first bank the default; the form says so.
    const firstBank = !editId && bankCount === 0;
    const blocksDelete = (item) => item.is_default === 1 && bankCount > 1;

    // Shared by the list and grid views.
    const renderCategory = (item) => (
        <span className={`badge fw-medium px-8 py-4 radius-4 text-xs ${item.account_category === 'CURRENT' ? 'bg-info-50 text-info-600' : 'bg-success-50 text-success-600'}`}>
            {item.account_category || '-'}
        </span>
    );

    const renderGlAccount = (item) => (item.gl_code ? (
        <div>
            <span className="badge bg-primary-50 text-primary-600 fw-medium px-8 py-4 radius-4 text-xs">
                {item.gl_code}
            </span>
            {item.glAccount?.name && (
                <div className="text-xs text-secondary-light mt-2">{item.glAccount.name}</div>
            )}
        </div>
    ) : '-');

    const renderDefault = (item) => (item.is_default === 1 ? (
        <span className="badge bg-success-50 text-success-600 fw-medium px-10 py-4 radius-4">
            <Icon icon="mdi:check-circle" className="me-1" /> Default
        </span>
    ) : (
        <button
            className="btn btn-sm btn-outline-secondary px-8 py-4 radius-4 text-xs text-nowrap"
            onClick={() => handleSetDefault(item)}
            disabled={updateLoading}
        >
            Set Default
        </button>
    ));

    const renderActions = (item) => (
        <div className="d-flex align-items-center justify-content-end gap-2">
            <button
                className="btn btn-sm btn-outline-primary px-8 py-4 radius-4"
                onClick={() => handleEdit(item)}
                title="Edit"
            >
                <Icon icon="mdi:pencil" />
            </button>
            <button
                className="btn btn-sm btn-outline-danger px-8 py-4 radius-4"
                onClick={() => handleDelete(item)}
                title={blocksDelete(item) ? 'Set another bank as the default before deleting this one' : 'Delete'}
                disabled={deleteLoading || blocksDelete(item)}
            >
                <Icon icon="mdi:delete" />
            </button>
        </div>
    );

    return (
        <div className="invoice-setup-page">
            {/* No trail: "Setup" is a menu group with no page of its own, so the
                crumb led to the portal root and bounced to the login page. */}
            <Breadcrumb
                headingTitle="Bank Accounts"
                description="Manage society bank accounts for receipt processing"
                showTrail={false}
            />

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:bank" className="text-primary-600 text-xl" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> GL codes are auto-generated when you add a bank account. Mark one bank as default for receipt processing. Cash payments use GL 01-01-01-01 automatically.
                    </p>
                </div>
            </div>

            {!loading && !hasDefault && (
                <div className="alert alert-warning d-flex align-items-start gap-2 text-sm mb-16" role="alert">
                    <Icon icon="mdi:alert-outline" className="text-lg flex-shrink-0 mt-1" />
                    <span>
                        No default bank is set. Residents can't record UPI, NEFT or cheque payments from the app
                        until one is: {bankCount === 0 ? 'add a bank account.' : 'click Set Default on a bank.'}
                    </span>
                </div>
            )}

            {/* Actions */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-12">
                <button
                    onClick={() => setShowModal(true)}
                    className="btn d-flex align-items-center gap-2 px-10 py-4 radius-6 text-white"
                    style={{ backgroundColor: '#f59e0b' }}
                    disabled={loading}
                >
                    <Icon icon="mdi:plus-circle" className="text-base" />
                    Add Bank Account
                </button>
                {/* Phones always get the cards: the table's Set Default and actions
                    columns sit well off-screen there. */}
                <div className="btn-group d-none d-md-inline-flex" role="group">
                    <button
                        type="button"
                        className={`btn btn-sm d-flex align-items-center gap-1 px-12 py-6 ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('list')}
                    >
                        <Icon icon="mdi:view-list" className="text-lg" />
                        List
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm d-flex align-items-center gap-1 px-12 py-6 ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('grid')}
                    >
                        <Icon icon="mdi:view-grid" className="text-lg" />
                        Grid
                    </button>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="text-center py-40">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            {/* Bank Accounts Table */}
            {!loading && (
                <div>
                    {accounts.length === 0 && (
                        <div className="card radius-8 border">
                            <div className="card-body p-24 text-center">
                                <Icon icon="mdi:bank-off" className="text-2xl text-secondary-light mb-12" />
                                <p className="text-xs text-secondary-light mb-0">No bank accounts found. Click "Add Bank Account" to get started.</p>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive d-none d-md-block">
                        {accounts.length > 0 && viewMode === 'list' && (
                            <table className="table table-hover align-middle">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">#</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Bank Name</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Category</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Branch</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Account Number</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">IFSC</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">GL Account</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Default</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="ps-12 py-8">{index + 1}</td>
                                            <td className="py-16">
                                                <div className="fw-medium">{item.bank_name}</div>
                                                {item.account_holder_name && (
                                                    <div className="text-xs text-secondary-light">{item.account_holder_name}</div>
                                                )}
                                            </td>
                                            <td className="py-16">{renderCategory(item)}</td>
                                            <td className="py-8 text-secondary-light">{item.branch_name || '-'}</td>
                                            <td className="py-16">
                                                <span className="font-monospace text-sm">{item.account_number || '-'}</span>
                                            </td>
                                            <td className="py-16">
                                                <span className="font-monospace text-sm">{item.ifsc_code || '-'}</span>
                                            </td>
                                            <td className="py-16">{renderGlAccount(item)}</td>
                                            <td className="py-16">{renderDefault(item)}</td>
                                            <td className="pe-12 py-8 text-end">{renderActions(item)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Grid View */}
                    {accounts.length > 0 && (
                        <div className={`row g-3 ${viewMode === 'grid' ? '' : 'd-md-none'}`}>
                            {accounts.map((item) => (
                                <div key={item.id} className="col-12 col-md-6 col-xxl-4">
                                    <div className="card radius-12 border-0 shadow-sm h-100">
                                        <div className="card-header bg-base border-bottom d-flex align-items-center justify-content-between gap-2">
                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                                <div className="w-40-px h-40-px rounded-circle bg-primary-50 d-flex align-items-center justify-content-center flex-shrink-0">
                                                    <Icon icon="mdi:bank" className="text-primary-600 text-xl" />
                                                </div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div className="fw-semibold text-primary-light text-truncate">{item.bank_name}</div>
                                                    {item.account_holder_name && (
                                                        <div className="text-xs text-secondary-light text-truncate">{item.account_holder_name}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0">{renderCategory(item)}</div>
                                        </div>
                                        <div className="card-body d-flex flex-column">
                                            <div className="row g-3 mb-16">
                                                <div className="col-6">
                                                    <div className="text-xs text-secondary-light mb-1">Account Number</div>
                                                    <span className="font-monospace text-sm text-break">{item.account_number || '-'}</span>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-xs text-secondary-light mb-1">IFSC</div>
                                                    <span className="font-monospace text-sm text-break">{item.ifsc_code || '-'}</span>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-xs text-secondary-light mb-1">Branch</div>
                                                    <span className="text-sm text-break">{item.branch_name || '-'}</span>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-xs text-secondary-light mb-1">GL Account</div>
                                                    {renderGlAccount(item)}
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between gap-2 mt-auto pt-12 border-top">
                                                {renderDefault(item)}
                                                {renderActions(item)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {banksPagination && banksPagination.totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-16">
                            <span className="text-xs text-secondary-light">
                                Showing page {banksPagination.page} of {banksPagination.totalPages} ({banksPagination.total} total)
                            </span>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => p - 1)}
                                >
                                    Previous
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page >= banksPagination.totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            <BankAccountModal
                defaultLocked={editingDefault || firstBank}
                defaultNote={editingDefault
                    ? 'This is the default bank. To change it, set another bank as the default.'
                    : firstBank ? 'The first bank is the default.' : null}
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                loading={createLoading || updateLoading}
            />
        </div>
    );
};

export default BankAccounts;
